import { converterDataHoraBr } from "_utils/Data";
import { RedirectDetalhes } from "components/RedirectDetalhes";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { DetalhesContainer, StatusSolicitacaoLicenciamento } from "./style";
import { FaCheck } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { utils, writeFileXLSX } from "xlsx";
import { Paginar } from "_utils/enuns/Paginar.enum";

export interface IDadosListarLoteLicenciamento {
  id: number;
  dataHora: string;
  nomeUsuarioSolicitante: string;
  nomeEmpresa: string;
  descricao: string;
  uf: string;
  qtdPlacasSolicitadas: number;
  qtdPlacasEmitidas: number;
}

interface Props {
  dados: IDadosListarLoteLicenciamento[];
  loading: boolean;
  onChangePage: (page: number) => void;
  totalRows: number;
  onChangeRowsPerPage: (limit: number) => void;
  rowsPerPage: number;
  pagina: "emissão" | "consulta";
  listarSolicitacoesLicenciamento: (paginar: number) => Promise<IDadosListarLoteLicenciamento[]>;
}

function handleStatusEmissao(qtdSolicitados: number, qtdEmitidos: number) {
  if (qtdEmitidos >= qtdSolicitados) {
    return (
      <StatusSolicitacaoLicenciamento type="finalizado">
        Finalizado <FaCheck />
      </StatusSolicitacaoLicenciamento>
    );
  }

  return (
    <StatusSolicitacaoLicenciamento type="processando">
      Processando <FontAwesomeIcon icon={faSpinner} spin />
    </StatusSolicitacaoLicenciamento>
  );
}

function makeRedirect(path: string) {
  return (
    <DetalhesContainer>
      <RedirectDetalhes path={path} target="_blank" />
    </DetalhesContainer>
  );
}

export function Tabela({
  dados,
  loading,
  onChangePage,
  onChangeRowsPerPage,
  rowsPerPage,
  totalRows,
  pagina = "emissão",
  listarSolicitacoesLicenciamento,
}: Readonly<Props>) {
  function handleStatusEmissaoPlanilha(
    qtdConsultados: number,
    qtdSolicitados: number,
  ) {
    if (qtdConsultados === qtdSolicitados) {
      return "FINALIZADO";
    }

    return "PROCESSANDO";
  }

  async function handleExport() {
    const solicitacoes = await listarSolicitacoesLicenciamento(Paginar.NAO);
    const dadosPlanilha = solicitacoes.map(
      ({
        dataHora,
        descricao,
        nomeEmpresa,
        id,
        nomeUsuarioSolicitante,
        uf,
        qtdPlacasEmitidas,
        qtdPlacasSolicitadas,
      }: IDadosListarLoteLicenciamento) => ({
        id,
        "Data/Hora Solicitação": converterDataHoraBr(new Date(dataHora)),
        "Usuário solicitante": nomeUsuarioSolicitante,
        Empresa: nomeEmpresa,
        Descrição: descricao,
        UF: uf,
        "Qtd. Placas Solicitadas": qtdPlacasSolicitadas,
        "Qtd. Placas Emitidas": qtdPlacasEmitidas,
        "Status Emissão": handleStatusEmissaoPlanilha(
          qtdPlacasEmitidas,
          qtdPlacasSolicitadas,
        ),
      }),
    );
    const planilha = utils.json_to_sheet(dadosPlanilha);
    planilha["!cols"] = [
      { wch: 5 },
      { wch: 25 },
      { wch: 30 },
      { wch: 20 },
      { wch: 30 },
      { wch: 7 },
      { wch: 30 },
      { wch: 30 },
      { wch: 20 },
    ];
    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, planilha);
    writeFileXLSX(workBook, "solicitacoes-licenciamento.xlsx");
  }

  const columns: IColum<IDadosListarLoteLicenciamento>[] = [
    {
      name: <span>ID</span>,
      selector: ({ id }: IDadosListarLoteLicenciamento) => id,
      width: "50px",
    },
    {
      name: <span>Data/Hora Solicitação</span>,
      selector: ({ dataHora }: IDadosListarLoteLicenciamento) =>
        converterDataHoraBr(new Date(dataHora)),
      width: "170px",
    },
    {
      name: <span>Usuário solicitante</span>,
      selector: ({ nomeUsuarioSolicitante }: IDadosListarLoteLicenciamento) =>
        nomeUsuarioSolicitante ?? "--",
      width: "150px",
    },
    {
      name: <span>Empresa</span>,
      selector: ({ nomeEmpresa }: IDadosListarLoteLicenciamento) =>
        nomeEmpresa ?? "--",
      width: "180px",
    },
    {
      name: <span>Descrição</span>,
      selector: ({ descricao }: IDadosListarLoteLicenciamento) =>
        descricao ?? "--",
      width: "125px",
    },
    {
      name: <span>UF</span>,
      selector: ({ uf }: IDadosListarLoteLicenciamento) =>
        uf ? uf.toUpperCase() : "- -",
      width: "50px",
    },
    {
      name: <span>Qtd. Placas Solicitadas</span>,
      selector: ({ qtdPlacasSolicitadas }: IDadosListarLoteLicenciamento) =>
        qtdPlacasSolicitadas ?? 0,
    },
    {
      name: <span>Qtd. Placas Emitidas</span>,
      selector: ({ qtdPlacasEmitidas }: IDadosListarLoteLicenciamento) =>
        qtdPlacasEmitidas ?? 0,
    },
    {
      name: <span>Status da Emissão</span>,
      selector: ({
        qtdPlacasSolicitadas,
        qtdPlacasEmitidas,
      }: IDadosListarLoteLicenciamento) =>
        handleStatusEmissao(qtdPlacasSolicitadas, qtdPlacasEmitidas),
      width: "135px",
    },
    {
      name: <span>Detalhes</span>,
      selector: ({ id }: IDadosListarLoteLicenciamento) =>
        makeRedirect(`/backoffice/licenciamento/detalhes?idLote=${id}`),
      width: "80px",
    },
  ];

  return (
    <Table
      columns={columns}
      data={dados}
      loading={loading}
      keyField="id"
      pagination={true}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      rowsPerPage={rowsPerPage}
      totalRows={totalRows}
      exportExcel={true}
      handleExport={handleExport}
      buttonExportVariant="secondary"
    />
  );
}
